import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axiosClient from '../../api/axiosClient';
import InvoicePrintModal from '../../components/InvoicePrintModal';
import { useToast } from '../../context/ToastContext';
import { formatDate } from '../../utils/dateUtils';

const PurchaseList = () => {
  const toast = useToast();
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showPrintModal, setShowPrintModal] = useState(false);

  const fetchPurchases = async () => {
    setLoading(true);
    try {
      const res = await axiosClient.get(`/purchases?search=${search}&fromDate=${fromDate}&toDate=${toDate}`);
      if (res.success) {
        setPurchases(res.data || []);
      }
    } catch (err) {
      console.error('Error fetching purchases:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPurchases();
  }, []);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchPurchases();
  };

  const handlePrintPreview = async (id) => {
    try {
      const res = await axiosClient.get(`/purchases/${id}`);
      if (res.success) {
        setSelectedInvoice(res.data);
        setShowPrintModal(true);
      }
    } catch (err) {
      toast.error(err.message || 'Error loading purchase invoice.');
    }
  };

  const handleDelete = async (id, purchaseNo) => {
    if (window.confirm(`Are you sure you want to delete Purchase #${purchaseNo}?`)) {
      try {
        await axiosClient.delete(`/purchases/${id}`);
        toast.success(`Purchase #${purchaseNo} deleted successfully.`);
        fetchPurchases();
      } catch (err) {
        toast.error(err.message || 'Error deleting purchase.');
      }
    }
  };

  return (
    <div className="container-fluid p-0">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h3 className="fw-bold mb-0 text-dark">
            <i className="bi bi-file-earmark-text-fill text-primary me-2"></i> Purchases Invoices History
          </h3>
          <p className="text-muted small mb-0">Search, filter, view, print, or manage supplier purchase vouchers</p>
        </div>
        <Link to="/purchases/new" className="btn btn-ocean d-flex align-items-center gap-2">
          <i className="bi bi-bag-plus-fill"></i> New Purchase Entry
        </Link>
      </div>

      <div className="card shadow-sm border-0 mb-4">
        <div className="card-body p-3">
          <form className="row g-2 align-items-center" onSubmit={handleSearchSubmit}>
            <div className="col-12 col-md-4">
              <div className="input-group">
                <span className="input-group-text bg-light"><i className="bi bi-search"></i></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search supplier, purchase #, invoice #..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>

            <div className="col-6 col-md-2">
              <input
                type="date"
                className="form-control"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>

            <div className="col-6 col-md-2">
              <input
                type="date"
                className="form-control"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>

            <div className="col-6 col-md-2">
              <button type="submit" className="btn btn-primary w-100">Filter</button>
            </div>

            <div className="col-6 col-md-2 text-end">
              <span className="badge bg-light text-dark border p-2">Total Bills: {purchases.length}</span>
            </div>
          </form>
        </div>
      </div>

      <div className="card shadow-sm border-0">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: '80px' }}>Pur #</th>
                  <th>Date</th>
                  <th>Supplier Name</th>
                  <th>Type</th>
                  <th className="text-center">Items</th>
                  <th className="text-end">Grand Total</th>
                  <th className="text-center" style={{ width: '160px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="text-center py-5">
                      <div className="spinner-border text-primary" role="status"></div>
                    </td>
                  </tr>
                ) : purchases.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-5 text-muted">
                      No purchase records found.
                    </td>
                  </tr>
                ) : (
                  purchases.map((p) => (
                    <tr key={p.Id}>
                      <td>
                        <strong className="text-primary">#{p.purchaseNo}</strong>
                      </td>
                      <td>{formatDate(p.purchaseDate)}</td>
                      <td>
                        <strong className="text-dark">{p.SupplierName || 'Direct Fishermen'}</strong>
                        {p.SupplierMobile && <div className="small text-muted"><i className="bi bi-telephone me-1"></i>{p.SupplierMobile}</div>}
                      </td>
                      <td>
                        <span className={`badge ${p.invoiceType === 0 ? 'bg-success' : 'bg-warning text-dark'}`}>
                          {p.invoiceType === 0 ? 'Cash' : 'Credit'}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className="badge bg-light text-dark border">{p.itemCount} items</span>
                      </td>
                      <td className="text-end fw-bold text-dark">
                        ₹{parseFloat(p.GranadTotal).toFixed(2)}
                      </td>
                      <td className="text-center">
                        <div className="btn-group btn-group-sm">
                          <button
                            className="btn btn-outline-success"
                            onClick={() => handlePrintPreview(p.Id)}
                            title="Share on WhatsApp / PDF"
                          >
                            <i className="bi bi-whatsapp"></i>
                          </button>
                          <button
                            className="btn btn-outline-secondary"
                            onClick={() => handlePrintPreview(p.Id)}
                            title="Print Voucher / Download PDF"
                          >
                            <i className="bi bi-printer"></i>
                          </button>
                          <Link
                            to={`/purchases/edit/${p.Id}`}
                            className="btn btn-outline-primary"
                            title="Edit"
                          >
                            <i className="bi bi-pencil"></i>
                          </Link>
                          <button
                            className="btn btn-outline-danger"
                            onClick={() => handleDelete(p.Id, p.purchaseNo)}
                            title="Delete"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showPrintModal && (
        <InvoicePrintModal
          show={showPrintModal}
          onClose={() => setShowPrintModal(false)}
          invoiceData={selectedInvoice}
          type="purchase"
        />
      )}
    </div>
  );
};

export default PurchaseList;
